import { useState, useMemo, useCallback, useEffect, type ReactNode } from "react";
import { LuMailX } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import "./recipients.scss";
import "../Invoices/invoiceFilterBar.scss";
import "src/components/InvoiceStatus/invoiceStatus.scss";
import "../../styles/tables.scss";
import "src/components/Link/link.scss";

import { apiRequest } from "src/services/apiClient";
import Popup from "src/components/Popup/Popup";
import { useLiveRecipientsList } from "src/hooks/useLiveRecipientsList";
import { useSorting } from "src/hooks/useSorting";
import { SortIcon } from "src/components/SortIcon/SortIcon";
import PageHeader from "src/components/PageHeader/PageHeader";
import RosterSubBar from "src/components/PageHeader/RosterSubBar";
import TablePagination from "src/components/TablePagination/TablePagination";
import RecipientFilterBar, {
    RecipientFilters,
    DEFAULT_RECIPIENT_FILTERS,
} from "./RecipientFilterBar";
import TableSkeleton from "src/components/TableSkeleton/TableSkeleton";
import EmptyState from "src/components/EmptyState/EmptyState";
import CreateRecipientModal from "src/components/CreateRecipientModal/CreateRecipientModal";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import { RecipientRow } from "./RecipientRow";
import { PatientsWithoutRecipientSection } from "./PatientsWithoutRecipientSection";
import { formatSalutationLabel } from "src/helpers/salutation";
import { Recipient, Group } from "src/types";
import { DeleteInvoiceCascadeItem } from "src/components/DeleteInvoiceCascadeItem/DeleteInvoiceCascadeItem";
import {
    breakdownFromRecipient,
    sumInvoiceDeleteBreakdowns,
    formatOtherDoctorsWarning,
    mergeDoctorInvoiceBreakdowns,
} from "src/utils/invoiceDeleteBreakdown";
import { recipientMatchesSearch } from "src/helpers/recipientRosterPatients";
import { isRecipientSameAsPatient } from "src/helpers/recipientSamePerson";
import { runBulkInBatches } from "src/utils/bulkRunner";
import { isRecipientPostOnly } from "src/utils/postOnly";
import { useBulkSend } from "src/context/BulkSendContext";
import BulkQuestionnaireRecipientPreview from "./BulkQuestionnaireRecipientPreview";
import ManageGroupsPopup from "../Patients/ManageGroupsPopup";
import PatientGroupsPopup from "../Patients/PatientGroupsPopup";
import { useApi } from "src/hooks/useApi";

import { FaUserPlus, FaWpforms } from "react-icons/fa";
import { FaUserInjured } from "react-icons/fa6";
import { TbQrcode, TbX, TbTableImport, TbUsers, TbTag } from "react-icons/tb";
import { RiDeleteBin2Line } from "react-icons/ri";
import { useAuth } from "src/context/AuthProvider";
import { isAccounting, isAdmin as checkAdmin } from "src/utils/roles";
import { isDemoMode } from "src/demo/demoApi";
import CsvImportModal from "./CsvImportModal";

type RecipientSortKey = "name" | "patients" | "invoicesNotSent";
type RecipientViewKey = "all" | "withOpen" | "paid" | "noEmail" | "notSent" | "questionnaireNotSent";

// Kept as a named constant so the shared worker's rate stays easy to find/tune.
const QUESTIONNAIRE_BATCH_SIZE = 2;
const QUESTIONNAIRE_BATCH_DELAY_MS = 2000;

const getSortValue = (recipient: Recipient, key: RecipientSortKey): string | number => {
    switch (key) {
        case "name":            return `${recipient.lastName} ${recipient.firstName}`.toLowerCase();
        case "patients": {
            const pts = (recipient.patients ?? []).filter(Boolean) as any[];
            const p = pts.length > 0 ? pts[0] : null;
            return p ? `${p.lastName} ${p.firstName}`.toLowerCase() : `${recipient.lastName} ${recipient.firstName}`.toLowerCase();
        }
        case "invoicesNotSent": return recipient.invoicesNotSent ?? 0;
    }
};

export default function Recipients({ refreshTrigger = 0 }) {
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<RecipientFilters>(DEFAULT_RECIPIENT_FILTERS);
    const [viewKey, setViewKey] = useState<RecipientViewKey>("all");
    const [createOpen, setCreateOpen] = useState(false);
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);
    const [popup, setPopup] = useState<{ message: ReactNode; recipientId: string } | null>(null);
    const [patientPopup, setPatientPopup] = useState<{ message: ReactNode; patientId: string } | null>(null);
    const [questionnairePopup, setQuestionnairePopup] = useState<{ recipient: Recipient } | null>(null);
    const [questionnaireSending, setQuestionnaireSending] = useState(false);
    const [qrRecipient, setQrRecipient] = useState<Recipient | null>(null);
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [deletePhase, setDeletePhase] = useState<"deleting" | "cleaning" | null>(null);
    const [deleteCount, setDeleteCount] = useState(0);
    const [questionnairePopupState, setQuestionnairePopupState] = useState<{
        canSend: Recipient[]; noEmail: Recipient[]; postOnly: Recipient[]; selected: Set<string>;
    } | null>(null);
    const [bulkPopup, setBulkPopup] = useState<{
        title: string; message: ReactNode;
        action: () => void; confirmLabel: string; confirmVariant: "primary" | "danger"; confirmDisabled?: boolean;
        className?: string;
    } | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { enqueue } = useBulkSend();
    const { user } = useAuth();
    const role = user?.doctor?.userRights;
    const permRecipients = user?.doctor?.permRecipients;
    const roleDefault = role === 2 || role === 3; // Doctor + Admin
    const canWrite = permRecipients !== undefined ? permRecipients : roleDefault;

    // ?import=csv (onboarding checklist, quick tips) opens the CSV import straight away — it lives
    // in the "…" menu, which someone following a hint shouldn't have to hunt for. The param is
    // dropped right after, so a reload or Back doesn't reopen it.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.get("import") !== "csv") return;
        if (canWrite) setCsvImportOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("import");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, canWrite]);

    const isAdmin = checkAdmin(role);
    // Delete is a separate, grantable permission (Team Settings' None/Edit/Edit+Delete
    // control) — Admin-only by default, but not admin-exclusive once granted.
    const canDelete = user?.doctor?.permPatientsDelete !== undefined
        ? user.doctor.permPatientsDelete
        : isAdmin;
    const isPlus = (user?.praxis?.businessPlan ?? 1) >= 2;
    const { sortKey, sortDir, handleSort, applySorting } = useSorting<RecipientSortKey>();

    const { recipients, loading, error, reload } = useLiveRecipientsList();

    const { data: settingsData } = useApi<{ groupsEnabled?: boolean }>("/settings");
    const groupsEnabled = settingsData?.groupsEnabled === true;
    // Groups are a Plus+ feature (or admin-granted add-on), gated per-doctor by the
    // Tags & Groups view/edit permissions (admin-only by default, see TeamSettings).
    const groupsAvailable = (user?.praxis?.entitlements?.tagging ?? isPlus) && groupsEnabled;
    const canViewGroups = user?.doctor?.permTagsGroupsView !== undefined ? user.doctor.permTagsGroupsView : isAdmin;
    const canEditGroups = user?.doctor?.permTagsGroupsEdit !== undefined ? user.doctor.permTagsGroupsEdit : isAdmin;
    const showGroupsUI = canViewGroups && groupsAvailable;
    const { data: groupsData } = useApi<Group[]>(showGroupsUI ? "/groups" : null);
    const groups = groupsData ?? [];
    const groupMap = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
    const [showManageGroups, setShowManageGroups] = useState(false);
    const [assignGroupsPatient, setAssignGroupsPatient] = useState<{ id: string; groupIds: string[] } | null>(null);

    const handleAssignGroups = useCallback(async (patientId: string, groupIds: string[]) => {
        try {
            await apiRequest(`/patients/${patientId}/groups`, { method: "PATCH", body: JSON.stringify({ groupIds }) }, { "Content-Type": "application/json" });
            await reload();
        } catch {
            setToast({ message: t("groups.assignFailed"), variant: "error" });
        }
    }, [reload, t]);

    const [showBulkGroupsPopup, setShowBulkGroupsPopup] = useState(false);
    const [bulkGroupIds, setBulkGroupIds] = useState<string[]>([]);

    useEffect(() => {
        if (refreshTrigger > 0) void reload();
    }, [refreshTrigger, reload]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    }, []);

    const toggleSelectAll = useCallback((ids: string[]) => {
        setSelectedIds((prev) => {
            const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
            return allSelected ? new Set() : new Set(ids);
        });
    }, []);

    // Row IDs: patient IDs for multi-patient recipients, recipient ID for single-patient
    const rowIds = useCallback((r: Recipient) => {
        const pl = (r.patients ?? []).filter(Boolean);
        return pl.length > 1 ? pl.map((p: any) => p.id as string) : [r.id];
    }, []);

    // Map patient ID → recipient ID for resolving bulk delete targets
    const patientToRecipient = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of recipients) {
            const pl = (r.patients ?? []).filter(Boolean);
            if (pl.length > 1) for (const p of pl) map.set((p as any).id, r.id);
        }
        return map;
    }, [recipients]);

    const selectedRecipientIds = useMemo(() => {
        const ids = new Set<string>();
        for (const id of selectedIds) ids.add(patientToRecipient.get(id) ?? id);
        return ids;
    }, [selectedIds, patientToRecipient]);

    // Additive on the backend ($addToSet) — applying groups to a multi-select never
    // clears groups a patient already belongs to, only adds the newly picked ones.
    const applyBulkGroups = useCallback(async () => {
        if (bulkGroupIds.length === 0) { setShowBulkGroupsPopup(false); return; }
        // selectedIds mixes patient IDs (multi-patient recipients) and recipient IDs
        // (single-patient recipients, per rowIds) — resolve everything to patient IDs,
        // since Patient.groupIds is per-patient.
        const patientIds = [...selectedIds].flatMap((id) => {
            if (patientToRecipient.has(id)) return [id];
            const r = recipients.find((x) => x.id === id);
            const pl = (r?.patients ?? []).filter(Boolean);
            return pl.length === 1 ? [(pl[0] as any).id as string] : [];
        });
        if (patientIds.length === 0) { setShowBulkGroupsPopup(false); setBulkGroupIds([]); return; }
        try {
            await apiRequest(
                "/patients/bulk/groups",
                { method: "PATCH", body: JSON.stringify({ ids: patientIds, groupIds: bulkGroupIds }) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: t("groups.bulkGroupsApplied", { count: patientIds.length }), variant: "success" });
            setSelectedIds(new Set());
            await reload();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("groups.assignFailed"), variant: "error" });
        } finally {
            setShowBulkGroupsPopup(false);
            setBulkGroupIds([]);
        }
    }, [bulkGroupIds, selectedIds, patientToRecipient, recipients, reload, t]);

    const buildDeleteMessage = (r: Recipient) => {
        const patientList  = r.patients ?? [];
        const invoiceBreakdown = breakdownFromRecipient(r);
        const otherDoctorsWarning = formatOtherDoctorsWarning(t, r.invoiceDoctorBreakdown ?? []);
        const samePerson   = isRecipientSameAsPatient(r);
        const singlePatient = !samePerson && patientList.length === 1 ? patientList[0] : null;
        const displayName  = singlePatient
            ? `${singlePatient.firstName} ${singlePatient.lastName}`.trim()
            : `${formatSalutationLabel(r.salutation, t)} ${r.firstName} ${r.lastName}`.trim();
        return (
            <>
                <p>{t("recipients.deleteTitle")}:&nbsp;<strong>{displayName}</strong>?</p>
                {(patientList.length > 0 || invoiceBreakdown) && (
                    <div className="popup-warning">
                        <p style={{ margin: 0 }}>{t("recipients.deleteCascadeIntro")}</p>
                        <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                            {patientList.length > 0 && (
                                <li>
                                    {t(
                                        patientList.length === 1
                                            ? "recipients.deleteAlsoLinked_one"
                                            : "recipients.deleteAlsoLinked_other",
                                        { count: patientList.length },
                                    )}
                                </li>
                            )}
                            {invoiceBreakdown && (
                                <DeleteInvoiceCascadeItem t={t} breakdown={invoiceBreakdown} />
                            )}
                        </ul>
                    </div>
                )}
                {otherDoctorsWarning && (
                    <div className="popup-warning">
                        <p style={{ margin: 0 }}>⚠ {otherDoctorsWarning}</p>
                    </div>
                )}
            </>
        );
    };

    const handlePatientDeleteClick = useCallback((patient: any, _recipient: Recipient) => {
        const name = `${patient.firstName} ${patient.lastName}`.trim();
        const invoiceCount = (patient.invoicesOpenIds?.length ?? 0) + (patient.invoicesDoneIds?.length ?? 0);
        setPatientPopup({
            patientId: patient.id,
            message: (
                <>
                    <p>{t("recipients.deleteTitle")}:&nbsp;<strong>{name}</strong>?</p>
                    {invoiceCount > 0 && (
                        <div className="popup-warning">
                            <p style={{ margin: 0 }}>{t("recipients.deleteCascadeIntro")}</p>
                            <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                                <li>{t("recipients.deleteAlsoLinked_one", { count: 1 })}</li>
                            </ul>
                        </div>
                    )}
                </>
            ),
        });
    }, [t]);

    const bulkDelete = useCallback(() => {
        // Split: individual patients from partial multi-patient selections vs full recipient deletions
        const recipientIdsToDelete: string[] = [];
        const patientIdsToDelete: string[] = [];
        const handledRecipients = new Set<string>();

        for (const id of selectedIds) {
            const recipientId = patientToRecipient.get(id);
            if (recipientId) {
                if (handledRecipients.has(recipientId)) continue;
                handledRecipients.add(recipientId);
                const r = recipients.find((x) => x.id === recipientId);
                const allPids = (r?.patients ?? []).filter(Boolean).map((p: any) => p.id as string);
                if (allPids.every((pid) => selectedIds.has(pid))) {
                    recipientIdsToDelete.push(recipientId);
                } else {
                    for (const pid of allPids.filter((pid) => selectedIds.has(pid))) {
                        patientIdsToDelete.push(pid);
                    }
                }
            } else {
                recipientIdsToDelete.push(id);
            }
        }

        const selectedRecipients = recipients.filter((r) => recipientIdsToDelete.includes(r.id));
        const cascadePatients    = selectedRecipients.flatMap((r) => r.patients ?? []);
        const invoiceBreakdown   = sumInvoiceDeleteBreakdowns(
            selectedRecipients.map((r) => breakdownFromRecipient(r)).filter((b): b is NonNullable<typeof b> => !!b),
        );
        const otherDoctorsWarning = formatOtherDoctorsWarning(
            t,
            mergeDoctorInvoiceBreakdowns(selectedRecipients.map((r) => r.invoiceDoctorBreakdown ?? [])),
        );

        // Patients being deleted individually (partial selection from multi-patient recipients)
        const partialPatients = patientIdsToDelete.flatMap((pid) => {
            for (const r of recipients) {
                const p = (r.patients ?? []).find((x: any) => x.id === pid);
                if (p) return [p as any];
            }
            return [];
        });

        const totalCount = recipientIdsToDelete.length + patientIdsToDelete.length;

        setBulkPopup({
            title: t("recipients.deleteNTitle", { count: totalCount }),
            message: (
                <>
                    <ul className="bulk-popup-list">
                        {selectedRecipients.map((r) => <li key={r.id}><strong>{r.firstName} {r.lastName}</strong></li>)}
                        {partialPatients.map((p: any) => <li key={p.id}><strong>{p.firstName} {p.lastName}</strong></li>)}
                    </ul>
                    {(cascadePatients.length > 0 || invoiceBreakdown) && (
                        <div className="popup-warning">
                            <p style={{ margin: 0 }}>{t("recipients.deleteCascadeIntro")}</p>
                            <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                                {cascadePatients.length > 0 && (
                                    <li>
                                        {t(
                                            cascadePatients.length === 1
                                                ? "recipients.deleteAlsoLinked_one"
                                                : "recipients.deleteAlsoLinked_other",
                                            { count: cascadePatients.length },
                                        )}
                                    </li>
                                )}
                                {invoiceBreakdown && (
                                    <DeleteInvoiceCascadeItem t={t} breakdown={invoiceBreakdown} />
                                )}
                            </ul>
                        </div>
                    )}
                    {otherDoctorsWarning && (
                        <div className="popup-warning">
                            <p style={{ margin: 0 }}>⚠ {otherDoctorsWarning}</p>
                        </div>
                    )}
                </>
            ),
            confirmLabel: t("form.delete"),
            confirmVariant: "danger",
            action: async () => {
                setSelectedIds(new Set());
                setBulkDeleting(true);
                setDeleteCount(totalCount);
                setDeletePhase("deleting");
                try {
                    await apiRequest(`/recipients/bulk`, {
                        method: "DELETE",
                        body: JSON.stringify({ recipientIds: recipientIdsToDelete, patientIds: patientIdsToDelete }),
                    }, { "Content-Type": "application/json" });
                    setDeletePhase("cleaning");
                } finally {
                    await reload();
                    setBulkDeleting(false);
                    setDeletePhase(null);
                }
            },
        });
    }, [selectedIds, selectedRecipientIds, recipients, reload]);

    const bulkSendQuestionnaire = useCallback(() => {
        const selected = recipients.filter((r) => selectedRecipientIds.has(r.id));
        const postOnly = selected.filter((r) => isRecipientPostOnly(r));
        const canSend  = selected.filter((r) => !!r.email && !isRecipientPostOnly(r));
        const noEmail  = selected.filter((r) => !r.email && !isRecipientPostOnly(r));
        // Default-exclude anyone who already submitted — resending to them is almost never
        // wanted. Everyone else (never sent, or sent-but-not-yet-submitted) defaults to checked.
        const defaultSelected = new Set(
            canSend.filter((r) => r.questionnaireStatus !== "submitted").map((r) => r.id),
        );
        setQuestionnairePopupState({ canSend, noEmail, postOnly, selected: defaultSelected });
    }, [selectedRecipientIds, recipients]);

    const toggleQuestionnaireRecipient = useCallback((id: string) => {
        setQuestionnairePopupState((prev) => {
            if (!prev) return prev;
            const next = new Set(prev.selected);
            if (next.has(id)) next.delete(id); else next.add(id);
            return { ...prev, selected: next };
        });
    }, []);

    const toggleAllQuestionnaireRecipients = useCallback(() => {
        setQuestionnairePopupState((prev) => {
            if (!prev) return prev;
            const allSelected = prev.canSend.every((r) => prev.selected.has(r.id));
            return { ...prev, selected: allSelected ? new Set() : new Set(prev.canSend.map((r) => r.id)) };
        });
    }, []);

    const confirmSendQuestionnaire = useCallback(() => {
        if (!questionnairePopupState) return;
        const toSend = questionnairePopupState.canSend.filter((r) => questionnairePopupState.selected.has(r.id));
        setQuestionnairePopupState(null);
        setSelectedIds(new Set());
        if (isDemoMode()) {
            setToast({ message: t("common.demoSimulated"), variant: "success" });
            return;
        }
        // Batches of 2 with a pause between — the praxis's SMTP provider rejects with
        // "too many connections" even at low concurrency, since sendEmail() opens a fresh
        // connection per send and a just-closed one isn't necessarily released on the
        // server's side yet. Spacing batches out in time, not just capping concurrency,
        // is what actually respects that. A cancel only stops the *next* batch from
        // starting — never a failure, and never a batch already in flight.
        enqueue(t("common.bulkSendSubjectQuestionnaires"), async ({ onProgress, shouldCancel }) => {
            const { succeeded, failed, cancelled } = await runBulkInBatches(
                toSend,
                QUESTIONNAIRE_BATCH_SIZE,
                QUESTIONNAIRE_BATCH_DELAY_MS,
                (r) =>
                    apiRequest(
                        "/patient-portal/questionnaire/create-link",
                        { method: "POST", body: JSON.stringify({ recipientId: r.id }) },
                        { "Content-Type": "application/json" },
                    ),
                onProgress,
                shouldCancel,
            );
            if (failed.length > 0) {
                console.error("[confirmSendQuestionnaire] failures:", failed.map((f) => ({
                    recipientId: f.item.id,
                    error: f.error instanceof Error ? f.error.message : f.error,
                })));
            }
            const firstReason = failed[0]?.error instanceof Error ? (failed[0].error as Error).message : undefined;
            void reload();
            return {
                succeeded: succeeded.length,
                failed: failed.length,
                cancelled,
                message: cancelled
                    ? t("common.cancelledProgress", { done: succeeded.length + failed.length, total: toSend.length })
                    : failed.length > 0
                    ? (firstReason
                        ? t("patients.bulkQuestionnaireDonePartialReason", { sent: succeeded.length, total: toSend.length, failed: failed.length, reason: firstReason })
                        : t("patients.bulkQuestionnaireDonePartial", { sent: succeeded.length, total: toSend.length, failed: failed.length }))
                    : t("patients.bulkQuestionnaireDone", { count: succeeded.length }),
                variant: cancelled || failed.length > 0 ? "error" : "success",
                details: failed.map((f) => f.error instanceof Error ? f.error.message : String(f.error)),
            };
        });
    }, [questionnairePopupState, reload, t, enqueue]);

    const deleteRecipient = async (id: string) => {
        try {
            await apiRequest(`/recipients/${id}`, { method: "DELETE" }, { "Content-Type": "application/json" });
            await reload();
        } catch (err) {
            setToast({ message: t("recipients.failedDelete"), variant: "error" });
        }
    };

    const openQr = async (recipient: Recipient) => {
        setQrRecipient(recipient);
        setQrUrl(null);
        setQrLoading(true);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/create-qr`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ recipientId: recipient.id }),
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            setQrUrl(URL.createObjectURL(blob));
        } catch {
            setToast({ message: t("recipientPage.qrFailed"), variant: "error" });
            setQrRecipient(null);
        } finally {
            setQrLoading(false);
        }
    };

    const sendQuestionnaire = async (recipientId: string) => {
        setQuestionnaireSending(true);
        try {
            await apiRequest("/patient-portal/questionnaire/create-link", {
                method: "POST",
                body: JSON.stringify({ recipientId }),
            }, { "Content-Type": "application/json" });
            setQuestionnairePopup(null);
            setToast({ message: t("recipientPage.questionnaireSent"), variant: "success" });
            await reload();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("recipientPage.questionnaireFailed"), variant: "error" });
            setQuestionnairePopup(null);
        } finally {
            setQuestionnaireSending(false);
        }
    };

    const filteredRecipients = useMemo(() => applySorting(
        recipients.filter((r) => {
            if (search && !recipientMatchesSearch(r, search)) return false;
            if (filters.openInvoices && !((r.invoicesOpen ?? 0) > 0)) return false;
            if (filters.paidInvoices && !((r.invoicesDone ?? 0) > 0)) return false;
            if (filters.hasReceivedLink) {
                const sent = (r.invoicesDone ?? 0) + (r.invoicesOpen ?? 0) - (r.invoicesNotSent ?? 0);
                if (sent <= 0) return false;
            }
            if (filters.questionnaireSent && r.questionnaireStatus !== "submitted") return false;
            // "sent, not signed" — mirrors PatientFilterBar's questionnaireNotSubmitted:
            // link sent (or opened) but not yet submitted.
            if (filters.questionnaireNotSubmitted) {
                const status = r.questionnaireStatus;
                if (status !== "sent" && status !== "opened") return false;
            }
            if (filters.noQuestionnaireSent && r.questionnaireStatus && r.questionnaireStatus !== "not_sent") return false;
            if (filters.noEmail && r.email) return false;
            if (filters.noTel && (r.tel ?? []).length > 0) return false;
            if (filters.noAddress && (r.street || r.plz || r.city)) return false;
            if (filters.patient) {
                const match = (r.patients ?? []).some((p: any) =>
                    `${p.firstName} ${p.lastName}`.toLowerCase().includes(filters.patient.toLowerCase())
                );
                if (!match) return false;
            }
            if (filters.tel) {
                const match = (r.tel ?? []).some((t: string) =>
                    t.toLowerCase().includes(filters.tel.toLowerCase())
                );
                if (!match) return false;
            }
            if (filters.address) {
                const addr = `${r.street ?? ""} ${r.plz ?? ""} ${r.city ?? ""}`.toLowerCase();
                if (!addr.includes(filters.address.toLowerCase())) return false;
            }
            if (filters.email) {
                if (!(r.email ?? "").toLowerCase().includes(filters.email.toLowerCase())) return false;
            }
            if (filters.group) {
                const match = (r.patients ?? []).some((p: any) => (p.groupIds ?? []).includes(filters.group));
                if (!match) return false;
            }
            return true;
        }),
        getSortValue,
    ), [recipients, search, filters, sortKey, sortDir, applySorting]);

    const rowCount = (r: (typeof filteredRecipients)[number]) => {
        const pl = (r.patients ?? []).filter(Boolean);
        return pl.length > 1 ? pl.length : 1;
    };

    const totalPatientCount = useMemo(() =>
        filteredRecipients.reduce((sum, r) => sum + rowCount(r), 0),
    [filteredRecipients]);

    // Trim a recipient to only patients matching a specific view.
    // Returns null if no patients remain after filtering.
    const trimForView = (r: Recipient, view: RecipientViewKey): Recipient | null => {
        const pts = (r.patients ?? []).filter(Boolean);
        // When there are no patient sub-rows the recipient IS the single row —
        // fall back to recipient-level fields for the check.
        if (pts.length === 0) {
            switch (view) {
                case "withOpen": return (r.invoicesOpen  ?? 0) > 0 ? r : null;
                case "paid":     return (r.invoicesDone  ?? 0) > 0 ? r : null;
                case "noEmail":  return !r.email ? r : null;
                case "notSent":  return (r.invoicesNotSent ?? 0) > 0 ? r : null;
                case "questionnaireNotSent": return r.questionnaireStatus !== "submitted" ? r : null;
                default:         return r;
            }
        }
        let kept: typeof pts;
        switch (view) {
            case "withOpen": kept = pts.filter((p) => (p.invoicesOpen  ?? 0) > 0); break;
            case "paid":     kept = pts.filter((p) => (p.invoicesDone  ?? 0) > 0); break;
            case "noEmail":  kept = !r.email ? pts : []; break;
            case "notSent":  kept = pts.filter((p) => (p.invoicesNotSent ?? 0) > 0); break;
            case "questionnaireNotSent": kept = r.questionnaireStatus !== "submitted" ? pts : []; break;
            default:         return r;
        }
        return kept.length === 0 ? null : { ...r, patients: kept };
    };

    // Count patient rows produced by a given view filter
    const patientCountForView = (view: RecipientViewKey) =>
        filteredRecipients.reduce((sum, r) => {
            const trimmed = trimForView(r, view);
            if (!trimmed) return sum;
            const pl = (trimmed.patients ?? []).filter(Boolean);
            return sum + (pl.length > 1 ? pl.length : 1);
        }, 0);

    // Apply viewKey on top of filteredRecipients (patient-level trimming)
    const viewFilteredRecipients = useMemo(() =>
        viewKey === "all"
            ? filteredRecipients
            : filteredRecipients.map((r) => trimForView(r, viewKey)).filter((r): r is Recipient => r !== null),
    [filteredRecipients, viewKey]);

    // Tab counts match the actual patient rows shown per tab
    const tabCounts = useMemo(() => ({
        all:                  totalPatientCount,
        withOpen:             patientCountForView("withOpen"),
        paid:                 patientCountForView("paid"),
        noEmail:              patientCountForView("noEmail"),
        notSent:              patientCountForView("notSent"),
        questionnaireNotSent: patientCountForView("questionnaireNotSent"),
    }), [filteredRecipients, totalPatientCount]);

    const selectedRowCount = selectedIds.size;

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const PAGE_SIZES = [10, 25, 50, 100] as const;

    // Reset to page 0 when filters/search/view/sort change
    useEffect(() => { setPage(0); }, [search, filters, viewKey, sortKey, sortDir]);

    // Paginate by patient rows (not by recipient count) so "10 per page" means 10 patient rows
    const { paginatedRecipients, totalPages } = useMemo(() => {
        const startRow = page * pageSize;
        const endRow = startRow + pageSize;
        let counted = 0;
        const result: typeof viewFilteredRecipients = [];
        for (const r of viewFilteredRecipients) {
            if (counted >= endRow) break;
            const rows = rowCount(r);
            if (counted + rows > startRow) result.push(r);
            counted += rows;
        }
        return {
            paginatedRecipients: result,
            totalPages: Math.max(1, Math.ceil(totalPatientCount / pageSize)),
        };
    }, [viewFilteredRecipients, page, pageSize, totalPatientCount]);

    const now = new Date();
    const eyebrowTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const th = (label: ReactNode, key: RecipientSortKey, extraClass?: string) => (
        <th className={`sortable${extraClass ? ` ${extraClass}` : ""}`} onClick={() => handleSort(key)}>
            {label}
            <SortIcon column={key} sortKey={sortKey} sortDir={sortDir} />
        </th>
    );

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            <div className="page page--roster patients-page">
            <div className="roster-body">
                <PageHeader
                    introOnly
                    sticky
                    loading={loading}
                    eyebrow={t("nav.patients")}
                    eyebrowIcon={<FaUserInjured />}
                    eyebrowExtras={loading ? undefined : [
                        t("common.updatedAt", { time: eyebrowTime, defaultValue: `Updated ${eyebrowTime}` }),
                        `${filteredRecipients.length} ${t("nav.recipients").toLowerCase()}`,
                    ]}
                    title={t("nav.patients")}
                    count={loading ? undefined : totalPatientCount}
                    lead={
                        !loading ? (
                            <Trans
                                i18nKey="recipients.lead"
                                values={{ total: totalPatientCount, recipientCount: filteredRecipients.length, withOpen: tabCounts.withOpen, noEmail: tabCounts.noEmail }}
                                components={{ strong: <strong /> }}
                            />
                        ) : undefined
                    }
                    search={search}
                    setSearch={setSearch}
                    placeholder={t("recipients.search")}
                    primaryAction={canWrite ? {
                        label: <><FaUserPlus /> {t("recipients.add")}</>,
                        onClick: () => setCreateOpen(true),
                    } : undefined}
                    // A one-off (moving patients over from the old software), so it sits in "…"
                    // like the Invoices page's export, not as a button on a page used every day.
                    // Group management is occasional setup — menu, like Tags on Invoices.
                    overflowItems={[
                        ...(showGroupsUI && canEditGroups ? [
                            { label: <><TbUsers /> {t("groups.manageGroups")}</>, onClick: () => setShowManageGroups(true) },
                        ] : []),
                        ...(canWrite ? [
                            { label: <><TbTableImport /> {t("recipients.importCsv")}</>, onClick: () => setCsvImportOpen(true) },
                        ] : []),
                    ]}
                    accentColor="#22c55e"
                />

                <RosterSubBar
                    loading={loading}
                    tabs={[
                        {
                            key: "all",
                            label: t("invoices.viewAll", { defaultValue: "All" }),
                            count: tabCounts.all,
                            active: viewKey === "all",
                            onClick: () => setViewKey("all"),
                        },
                        {
                            key: "withOpen",
                            label: t("patients.viewWithOpen", { defaultValue: "With open invoices" }),
                            count: tabCounts.withOpen,
                            warn: true,
                            active: viewKey === "withOpen",
                            onClick: () => setViewKey("withOpen"),
                        },
                        {
                            key: "paid",
                            label: t("patients.viewAllPaid", { defaultValue: "All paid up" }),
                            count: tabCounts.paid,
                            active: viewKey === "paid",
                            onClick: () => setViewKey("paid"),
                        },
                        {
                            key: "noEmail",
                            label: t("patients.viewMissingEmail", { defaultValue: "Missing email" }),
                            count: tabCounts.noEmail,
                            active: viewKey === "noEmail",
                            onClick: () => setViewKey("noEmail"),
                        },
                        {
                            key: "notSent",
                            label: t("recipients.showInvoiceNotSent", { defaultValue: "Invoice not sent" }),
                            count: tabCounts.notSent,
                            active: viewKey === "notSent",
                            onClick: () => setViewKey("notSent"),
                        },
                        {
                            key: "questionnaireNotSent",
                            label: t("recipients.showQuestionnaireNotSubmitted", { defaultValue: "Fragebogen nicht eingereicht anzeigen" }),
                            count: tabCounts.questionnaireNotSent,
                            active: viewKey === "questionnaireNotSent",
                            onClick: () => setViewKey("questionnaireNotSent"),
                        },
                    ]}
                    subRightActions={
                        <RecipientFilterBar filters={filters} onChange={setFilters} groups={showGroupsUI ? groups : []} />
                    }
                    accentColor="#22c55e"
                />

                {!isDemoMode() && (
                    <PatientsWithoutRecipientSection
                        canWrite={canWrite}
                        canDelete={canDelete}
                        onAssigned={() => {
                            reload();
                            setToast({ message: t("recipients.noRecipient.assigned"), variant: "success" });
                        }}
                    />
                )}

                {!loading && viewFilteredRecipients.length > 0 && (
                    <TablePagination
                        placement="roster-top"
                        accentColor="#22c55e"
                        page={page}
                        setPage={(p) => { setSelectedIds(new Set()); setPage(p); }}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalPages={totalPages}
                        totalCount={totalPatientCount}
                        pageSizes={PAGE_SIZES}
                    />
                )}

                {selectedIds.size > 0 && canWrite && (
                    <div className="bulk-bar">
                        <span className="bulk-bar__count">{selectedIds.size} {t("invoices.selected")}</span>
                        <button className="bulk-bar__btn bulk-bar__btn--send" onClick={bulkSendQuestionnaire}>
                            <FaWpforms /> {t("patients.bulkQuestionnaireBtn")}
                        </button>
                        {showGroupsUI && canEditGroups && (
                            <button className="bulk-bar__btn bulk-bar__btn--tag" onClick={() => setShowBulkGroupsPopup(true)}>
                                <TbTag /> {t("groups.addToGroup")}
                            </button>
                        )}
                        {canDelete && (
                            <>
                                <span className="bulk-bar__sep" />
                                <button className="bulk-bar__btn bulk-bar__btn--delete" onClick={bulkDelete}>
                                    <RiDeleteBin2Line /> {t("form.delete")}
                                </button>
                            </>
                        )}
                        <span className="bulk-bar__sep" />
                        <button className="bulk-bar__btn bulk-bar__btn--clear" onClick={() => setSelectedIds(new Set())}>×</button>
                    </div>
                )}

                <div className={`table-container${bulkDeleting ? " table-container--busy" : ""}`}>
                        {bulkDeleting && (
                            <div className="table-busy-overlay">
                                <div className="table-busy-overlay__inner">
                                    <span className="table-busy-spinner" />
                                    <span className="table-busy-label">
                                        {deletePhase === "deleting"
                                            ? t("common.deletingData", { count: deleteCount })
                                            : t("common.cleaningUp")}
                                    </span>
                                </div>
                            </div>
                        )}
                    {loading ? (
                        <TableSkeleton cols={6} />
                    ) : error ? (
                        <div className="empty-state">{error}</div>
                    ) : (
                        <table className={`dashboard-table recipients-table${showGroupsUI ? " recipients-table--with-groups" : ""}`}>
                            <thead>
                                <tr>
                                    <th className="row-check">
                                        <input
                                            type="checkbox"
                                            checked={paginatedRecipients.length > 0 && paginatedRecipients.flatMap(rowIds).every((id) => selectedIds.has(id))}
                                            onChange={() => toggleSelectAll(paginatedRecipients.flatMap(rowIds))}
                                        />
                                    </th>
                                    {th(t("dashboard.patients"), "patients")}
                                    <th className="col-info recipients-table__info-col">{t("table.info")}</th>
                                    {th(t("table.billingRecipient"), "name")}
                                    {th(<span className="th-icon-label"><LuMailX /> {t("table.invNotSent")}</span>, "invoicesNotSent")}
                                    {showGroupsUI && <th>{t("groups.group")}</th>}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewFilteredRecipients.length === 0 ? (
                                    <tr><td colSpan={showGroupsUI ? 7 : 6}><EmptyState message={t("recipients.noFound")} /></td></tr>
                                ) : paginatedRecipients.map((recipient) => (
                                    <RecipientRow
                                        key={recipient.id}
                                        recipient={recipient}
                                        navigate={navigate}
                                        onDeleteClick={canDelete ? (r) => setPopup({ recipientId: r.id, message: buildDeleteMessage(r) }) : undefined}
                                        onPatientDeleteClick={canDelete ? handlePatientDeleteClick : undefined}
                                        onQuestionnaireClick={(r) => setQuestionnairePopup({ recipient: r })}
                                        onQrClick={openQr}
                                        showGroupsUI={showGroupsUI}
                                        groupMap={groupMap}
                                        onManageGroups={(patient) => setAssignGroupsPatient({ id: patient.id, groupIds: patient.groupIds ?? [] })}
                                        selectedIds={selectedIds}
                                        onToggle={toggleSelect}
                                        canWrite={canWrite}
                                        orderedPatientRows={viewFilteredRecipients.flatMap((r) => {
                                            const pts = (r.patients ?? []).filter(Boolean);
                                            if (pts.length > 1) return pts.map((p: any) => ({ recipientId: r.id, patientId: p.id as string | null, firstName: p.firstName, lastName: p.lastName }));
                                            if (pts.length === 1) { const p = pts[0] as any; return [{ recipientId: r.id, patientId: p.id as string | null, firstName: p.firstName, lastName: p.lastName }]; }
                                            return [{ recipientId: r.id, patientId: null as string | null, firstName: r.firstName, lastName: r.lastName }];
                                        })}
                                    />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

                {createOpen && (
                    <CreateRecipientModal
                        onClose={() => setCreateOpen(false)}
                        onCreated={() => {
                            if (!isDemoMode()) reload();
                            setToast({
                                message: isDemoMode() ? t("common.demoSimulated") : t("recipients.createdSuccess"),
                                variant: "success",
                            });
                        }}
                    />
                )}

                {csvImportOpen && (
                    <CsvImportModal
                        onClose={() => setCsvImportOpen(false)}
                        onImported={() => {
                            if (!isDemoMode()) reload();
                        }}
                    />
                )}
                {bulkPopup && (
                    <Popup
                        title={bulkPopup.title}
                        message={bulkPopup.message}
                        className={bulkPopup.className}
                        actions={[
                            { label: t("popup.cancel"), variant: "default", onClick: () => setBulkPopup(null) },
                            { label: bulkPopup.confirmLabel, variant: bulkPopup.confirmVariant, disabled: bulkPopup.confirmDisabled, onClick: () => { bulkPopup.action(); setBulkPopup(null); } },
                        ]}
                        onClose={() => setBulkPopup(null)}
                    />
                )}
                {questionnairePopupState && (
                    <Popup
                        className="popup--wide"
                        title={t("patients.bulkQuestionnaireTitle", { count: questionnairePopupState.canSend.length })}
                        message={
                            <BulkQuestionnaireRecipientPreview
                                canSend={questionnairePopupState.canSend}
                                noEmail={questionnairePopupState.noEmail}
                                postOnly={questionnairePopupState.postOnly}
                                selected={questionnairePopupState.selected}
                                onToggle={toggleQuestionnaireRecipient}
                                onToggleAll={toggleAllQuestionnaireRecipients}
                            />
                        }
                        actions={[
                            { label: t("popup.cancel"), variant: "default", onClick: () => setQuestionnairePopupState(null) },
                            {
                                label: `${t("patients.bulkQuestionnaireSend")} (${questionnairePopupState.selected.size})`,
                                variant: "primary",
                                disabled: questionnairePopupState.selected.size === 0,
                                onClick: confirmSendQuestionnaire,
                            },
                        ]}
                        onClose={() => setQuestionnairePopupState(null)}
                    />
                )}
                {popup && (
                    <Popup
                        title={t("recipients.deleteTitle")}
                        message={popup.message}
                        actions={[
                            { label: t("popup.cancel"), variant: "default", onClick: () => setPopup(null) },
                            {
                                label: t("popup.ok"),
                                variant: "danger",
                                onClick: () => {
                                    void deleteRecipient(popup.recipientId).finally(() => setPopup(null));
                                },
                            },
                        ]}
                        onClose={() => setPopup(null)}
                    />
                )}
                {patientPopup && (
                    <Popup
                        title={t("patients.deleteNTitle", { count: 1 })}
                        message={patientPopup.message}
                        actions={[
                            { label: t("popup.cancel"), variant: "default", onClick: () => setPatientPopup(null) },
                            {
                                label: t("form.delete"),
                                variant: "danger",
                                onClick: () => {
                                    void apiRequest(`/patients/${patientPopup.patientId}`, { method: "DELETE" }, { "Content-Type": "application/json" })
                                        .then(() => reload())
                                        .finally(() => setPatientPopup(null));
                                },
                            },
                        ]}
                        onClose={() => setPatientPopup(null)}
                    />
                )}
                {qrRecipient && (
                    <div className="qr-modal-overlay" onClick={() => setQrRecipient(null)}>
                        <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="qr-modal__header">
                                <span className="qr-modal__title"><TbQrcode /> {t("recipientPage.questionnaireQr")}</span>
                                <button className="qr-modal__close" onClick={() => setQrRecipient(null)}><TbX /></button>
                            </div>
                            <div className="qr-modal__body">
                                <p className="qr-modal__hint">{t("recipientPage.questionnaireQrHint")}</p>
                                {qrLoading && <div className="qr-modal__loading"><span className="skeleton" style={{ width: 280, height: 280, borderRadius: 8 }} /></div>}
                                {qrUrl && <img className="qr-modal__img" src={qrUrl} alt="QR Code" />}
                                <p className="qr-modal__name">{qrRecipient.firstName} {qrRecipient.lastName}</p>
                            </div>
                        </div>
                    </div>
                )}
                {questionnairePopup && (
                    <Popup
                        title={questionnairePopup.recipient.questionnaireStatus === "sent" || questionnairePopup.recipient.questionnaireStatus === "opened" || questionnairePopup.recipient.questionnaireStatus === "submitted"
                            ? t("recipients.questionnaireResend")
                            : t("recipients.sendQuestionnaire")}
                        message={
                            <p>
                                {t("recipients.sendQuestionnaireTo")}{" "}
                                <strong>
                                    {questionnairePopup.recipient.firstName} {questionnairePopup.recipient.lastName}
                                </strong>
                                {questionnairePopup.recipient.email
                                    ? <> {t("common.at")} <strong>{questionnairePopup.recipient.email}</strong></>
                                    : ` ${t("recipients.noEmail")}`}
                                ?
                            </p>
                        }
                        actions={[
                            { label: t("popup.cancel"), variant: "default", onClick: () => setQuestionnairePopup(null) },
                            {
                                label: questionnaireSending ? t("recipients.sending") : t("recipients.send"),
                                variant: "primary",
                                onClick: () => sendQuestionnaire(questionnairePopup.recipient.id),
                            },
                        ]}
                        onClose={() => setQuestionnairePopup(null)}
                    />
                )}
                {showManageGroups && <ManageGroupsPopup onClose={() => setShowManageGroups(false)} />}
                {assignGroupsPatient && (
                    <PatientGroupsPopup
                        groups={groups}
                        currentGroupIds={assignGroupsPatient.groupIds}
                        onChange={(groupIds) => handleAssignGroups(assignGroupsPatient.id, groupIds)}
                        onClose={() => setAssignGroupsPatient(null)}
                    />
                )}
                {showBulkGroupsPopup && (
                    <PatientGroupsPopup
                        groups={groups}
                        currentGroupIds={bulkGroupIds}
                        onChange={setBulkGroupIds}
                        onClose={applyBulkGroups}
                    />
                )}
            </div>
        </>
    );
}
